import { useQuery } from '@apollo/client'
import { AbstractToken } from '@swarm/types/tokens'
import {
  DotcAsset as DotcAssetSubgraph,
  Volume24hsDocument,
} from '@swarm/types/x-subgraph/graphql'
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import Big from 'big.js'
import { useMemo } from 'react'

import useRequest from '@core/hooks/async/useRequest'
import xClient from '@core/services/apollo/x-client'
import { useExchangeRates } from '@core/services/exchange-rates'
import { mapBy } from '@core/shared/utils'
import { big } from '@core/shared/utils/helpers'
import { getBlockNumberDaysAgo, unifyAddressToId } from '@core/web3'

type TradedVolume = Pick<DotcAssetSubgraph, 'id' | 'tradedVolume'>

const useTokensVolume24hs = (tokens: Pick<AbstractToken, 'id'>[] = []) => {
  const exchangeRates = useExchangeRates()
  const { data: blockOneDayAgo, loading: blockCalcLoading } = useRequest(
    getBlockNumberDaysAgo,
  )

  const tokensAddresses = useMemo(
    () => tokens.map((t) => unifyAddressToId(t.id)),
    [tokens],
  )

  const { data, loading } = useQuery(Volume24hsDocument, {
    fetchPolicy: 'no-cache',
    variables: {
      filter: {
        // eslint-disable-next-line camelcase
        id_in: tokensAddresses,
      },
      block: {
        number: blockOneDayAgo,
      },
    },
    skip: blockCalcLoading || !tokens.length,
    client: xClient,
  })

  const [currVolumeDictionary, prevVolumeDictionary]: [
    Map<string, TradedVolume>,
    Map<string, TradedVolume>,
  ] = useMemo(
    () => [
      mapBy(data?.currTradedVolume || [], 'id'),
      mapBy(data?.prevTradedVolume || [], 'id'),
    ],
    [data?.currTradedVolume, data?.prevTradedVolume],
  )

  const volumes: Map<string, Big> = useMemo(() => {
    const totalVolumes = new Map<string, Big>()
    if (loading || !tokens.length) return totalVolumes

    tokens.forEach((token) => {
      const tokenExchangeRate = exchangeRates[token.id]?.exchangeRate || 0
      const currTradedVolume =
        currVolumeDictionary.get(token.id)?.tradedVolume || 0
      const prevTradedVolume =
        prevVolumeDictionary.get(token.id)?.tradedVolume || 0

      const periodTradedVolume = big(currTradedVolume).minus(prevTradedVolume)

      const token24hsVolume = big(tokenExchangeRate).mul(periodTradedVolume)
      totalVolumes.set(token.id, token24hsVolume)
    })
    return totalVolumes
  }, [
    currVolumeDictionary,
    exchangeRates,
    loading,
    prevVolumeDictionary,
    tokens,
  ])

  return volumes
}

export default useTokensVolume24hs
